import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const STATUS_STYLES = {
  upcoming: 'bg-primary/10 text-primary border border-primary/20',
  ongoing: 'bg-amber-100 text-amber-700 border border-amber-200',
  completed: 'bg-green-100 text-green-700 border border-green-200',
};

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchEventDetails();
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      for (const path of ['./admin/api/events.php', '/admin/api/events.php']) {
        try {
          const res = await fetch(path);
          if (!res.ok) continue;
          const data = await res.json();
          if (data.success) {
            const found = data.data.find(e => parseInt(e.id) === parseInt(id));
            if (found) { setEvent(found); return; }
          }
        } catch { continue; }
      }
      setError('Event not found');
    } catch {
      setError('Error loading event details');
    } finally {
      setLoading(false);
    }
  };

  const fmtDate = (s) => new Date(s).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const fmtTime = (s) => s ? new Date(`2000-01-01T${s}`).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : '';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gray-50">
        <div className="spinner" style={{ width: 48, height: 48, borderWidth: 4 }} />
        <p className="text-gray-400 font-inter text-sm">Loading event details...</p>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <i className="fas fa-calendar-times text-accent text-3xl" />
          </div>
          <h3 className="font-poppins font-800 text-navy text-2xl mb-3">Event Not Found</h3>
          <p className="text-gray-400 font-inter text-sm mb-8">The event you're looking for doesn't exist or has been removed.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-primary text-white font-poppins font-600 text-sm rounded-xl hover:bg-primary-dark transition-colors">
              <i className="fas fa-home" /> Back to Home
            </button>
            <button onClick={() => window.location.reload()}
              className="flex items-center justify-center gap-2 px-5 py-2.5 bg-gray-100 text-gray-600 font-poppins font-600 text-sm rounded-xl hover:bg-gray-200 transition-colors">
              <i className="fas fa-redo" /> Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{event.name} — PAK School &amp; College System Events</title>
        <meta name="description" content={event.description || `Event: ${event.name} at PAK School & College System`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50 page-enter">
        {/* Back button */}
        <div className="bg-white border-b border-gray-100 sticky top-[70px] z-30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 text-gray-500 hover:text-primary font-poppins font-600 text-sm transition-colors"
            >
              <i className="fas fa-arrow-left text-xs" /> Back to Events
            </button>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid lg:grid-cols-3 gap-8">

            {/* Main */}
            <div className="lg:col-span-2 space-y-6">
              {/* Image */}
              {event.image_path && (
                <div className="relative rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src={`https://pakschoolandcollege.com/admin/${event.image_path}`}
                    alt={event.name}
                    className="w-full object-cover"
                    style={{ maxHeight: '480px' }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/30 to-transparent" />
                  {event.featured && (
                    <div className="absolute top-4 right-4 bg-amber-400 text-navy text-xs font-poppins font-700 px-3 py-1.5 rounded-full flex items-center gap-1.5 shadow-lg">
                      <i className="fas fa-star text-[10px]" /> Featured Event
                    </div>
                  )}
                </div>
              )}

              {/* Content card */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                <div className="flex items-start justify-between gap-4 mb-5">
                  <h1 className="font-poppins font-800 text-navy text-2xl sm:text-3xl leading-tight">{event.name}</h1>
                  <span className={`text-xs font-poppins font-700 px-3 py-1.5 rounded-full flex-shrink-0 ${STATUS_STYLES[event.status] || STATUS_STYLES.upcoming}`}>
                    {event.status?.charAt(0).toUpperCase() + event.status?.slice(1)}
                  </span>
                </div>

                {/* Meta info */}
                <div className="grid sm:grid-cols-2 gap-4 mb-6 bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-calendar-alt text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 font-inter">Date</p>
                      <p className="font-poppins font-600 text-navy text-sm">{fmtDate(event.event_date)}</p>
                    </div>
                  </div>
                  {event.event_time && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-clock text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-inter">Time</p>
                        <p className="font-poppins font-600 text-navy text-sm">{fmtTime(event.event_time)}</p>
                      </div>
                    </div>
                  )}
                  {event.location && (
                    <div className="flex items-center gap-3 sm:col-span-2">
                      <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <i className="fas fa-map-marker-alt text-accent" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-inter">Location</p>
                        <p className="font-poppins font-600 text-navy text-sm">{event.location}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="font-poppins font-700 text-navy text-lg mb-3">About This Event</h2>
                  <p className="text-gray-500 font-inter text-base leading-relaxed">
                    {event.description || 'No description available for this event.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-poppins font-700 text-navy text-base mb-5 pb-3 border-b border-gray-100">Event Details</h3>
                <div className="space-y-4">
                  {[
                    { icon: 'fa-calendar', label: 'Date', value: fmtDate(event.event_date), color: 'bg-primary/10', iconColor: 'text-primary' },
                    event.event_time && { icon: 'fa-clock', label: 'Time', value: fmtTime(event.event_time), color: 'bg-green-100', iconColor: 'text-green-600' },
                    event.location && { icon: 'fa-map-marker-alt', label: 'Location', value: event.location, color: 'bg-accent/10', iconColor: 'text-accent' },
                    { icon: 'fa-info-circle', label: 'Status', value: event.status?.charAt(0).toUpperCase() + event.status?.slice(1), color: 'bg-purple-100', iconColor: 'text-purple-600' },
                    event.featured && { icon: 'fa-star', label: 'Type', value: 'Featured Event', color: 'bg-amber-100', iconColor: 'text-amber-600' },
                  ].filter(Boolean).map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className={`w-9 h-9 rounded-lg ${item.color} flex items-center justify-center flex-shrink-0`}>
                        <i className={`fas ${item.icon} ${item.iconColor} text-sm`} />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-inter">{item.label}</p>
                        <p className="font-inter font-600 text-navy text-sm">{item.value}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={() => navigate('/')}
                className="w-full flex items-center justify-center gap-2 py-3 bg-primary text-white font-poppins font-600 text-sm rounded-xl hover:bg-primary-dark transition-all hover:-translate-y-0.5 shadow-lg shadow-primary/25"
              >
                <i className="fas fa-arrow-left" /> View All Events
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EventDetails;
